/**
 * Spectral-flux onset envelope (W2-08 timing; ARCHITECTURE.md §3.2 `core/audio-dsp/onset.ts`, DESIGN.md §5.2).
 *
 * Envelope (hop 512 samples, `OnsetEnvelope` in core/audio-dsp/types.ts; env[i] belongs to music time i * hopSec):
 *  1. frames of `frameSize` samples (about 23 ms: 1024 at 44.1/48 kHz) under a periodic Hann window; frame i covers
 *     samples [i * hop - frameSize + lead, i * hop + lead) (see FRAME_LEAD), zero outside the signal;
 *  2. magnitude spectrum (real FFT), normalised so a full-scale sine reads about 1, from 30 Hz to 16 kHz, grouped
 *     into logarithmic bands (BANDS_PER_OCTAVE per octave, at least one FFT bin each; a band reads the mean
 *     magnitude of its bins), so every octave weighs about the same: with linear bins the few bins of a kick drum
 *     would be outvoted by the hundreds above 1 kHz, where hi-hats and cymbals live;
 *  3. logarithmic compression ln(1 + 1000 |X|) per band (quiet attacks count, loud sustained notes do not dominate);
 *  4. half-wave rectified difference to the previous frame, summed over the bands (spectral flux);
 *  5. a slowly varying floor (moving mean over about 0.2 s) is subtracted and the result rectified, so the envelope
 *     is peaks over silence;
 *  6. scaled so the largest value is 1.
 * The frame alignment puts the envelope peak of a sharp attack within a few milliseconds of the attack itself (the
 * tests measure it), so the envelope can be read in music seconds without a correction.
 *
 * `lowOnsetEnvelope`: the same envelope restricted to the low band (30-200 Hz: kick drum, bass). The offset
 * suggestion uses it to tell the beat from the off-beat when hi-hats sit between the beats.
 *
 * `fineOnsetCurve`: a high-resolution novelty (32-sample hop) of a short PCM excerpt, for offset refinement: the
 * positive log-energy step between consecutive 256-sample frames, stamped at the middle of the newly added hop.
 */

import { createRealFft, hannWindow, nextPow2 } from './fft';
import type { OnsetEnvelope } from './types';

/** Hop of the onset envelope in samples (protocol: "Spectral-flux onset envelope (hop 512)"). */
export const ONSET_HOP = 512;

/** Log compression factor applied to normalised magnitudes. */
const COMPRESSION = 1000;
/** Lowest and highest analysed frequency. */
const MIN_FREQ = 30;
const MAX_FREQ = 16000;
/** Logarithmic bands per octave of the flux (each band spans at least one FFT bin). */
export const BANDS_PER_OCTAVE = 6;
/** Upper edge of the low band (`lowOnsetEnvelope`): kick drums and bass. */
export const LOW_BAND_MAX_FREQ = 200;
/** Moving-mean floor window (seconds, total width). */
const FLOOR_WINDOW_SEC = 0.2;

/**
 * Samples the analysis window extends past the frame's time stamp, as a fraction of the frame size. With a causal
 * window (lead 0) the flux of an attack peaks a little after it; this lead centres the peak on the attack.
 */
export const FRAME_LEAD = 0.25;

export interface OnsetOptions {
  readonly hop?: number;
  /** Analysis frame size (power of two); default about 23 ms. */
  readonly frameSize?: number;
  /** Lead of the window past the time stamp, as a fraction of the frame size (default FRAME_LEAD). */
  readonly lead?: number;
  /** Analysed frequency range (default 30 Hz to 16 kHz). */
  readonly minFreq?: number;
  readonly maxFreq?: number;
  /** Progress 0..1 (called a few dozen times). */
  readonly onProgress?: (p: number) => void;
}

/** Default analysis frame size for a sample rate: the power of two closest to 23 ms (1024 at 44.1 and 48 kHz). */
export function defaultFrameSize(sampleRate: number): number {
  const target = Math.max(64, sampleRate * 0.0232);
  const up = nextPow2(target);
  const down = up / 2;
  return target / down < up / target ? down : up;
}

/**
 * Band edges (FFT bin indices) of the logarithmic filterbank between bins `k0` and `k1` (inclusive): band b covers
 * bins [edges[b], edges[b + 1]). Bands are `bandsPerOctave` per octave and at least one bin wide, so the lowest
 * bands are single bins.
 */
export function logBandEdges(k0: number, k1: number, bandsPerOctave = BANDS_PER_OCTAVE): Int32Array {
  const out: number[] = [];
  if (!(k1 >= k0) || !(k0 >= 1)) return Int32Array.from(out);
  const ratio = 2 ** (1 / Math.max(1, bandsPerOctave));
  let k = k0;
  out.push(k);
  while (k <= k1) {
    const next = Math.min(k1 + 1, Math.max(k + 1, Math.ceil(k * ratio)));
    out.push(next);
    k = next;
  }
  return Int32Array.from(out);
}

/** Raw (unnormalised) spectral flux per hop, over logarithmic bands (see the module header). */
export function spectralFlux(mono: Float32Array, sampleRate: number, opts: OnsetOptions = {}): Float64Array {
  const hop = opts.hop ?? ONSET_HOP;
  const n = opts.frameSize ?? defaultFrameSize(sampleRate);
  const lead = Math.round((opts.lead ?? FRAME_LEAD) * n);
  const frames = mono.length === 0 ? 0 : Math.floor(mono.length / hop) + 1;
  const out = new Float64Array(frames);
  if (frames === 0) return out;
  const fft = createRealFft(n);
  const win = hannWindow(n);
  const half = n >> 1;
  const binHz = sampleRate / n;
  const k0 = Math.max(1, Math.floor((opts.minFreq ?? MIN_FREQ) / binHz));
  const k1 = Math.max(k0, Math.min(half, Math.ceil((opts.maxFreq ?? MAX_FREQ) / binHz)));
  const edges = logBandEdges(k0, k1);
  const bands = edges.length - 1;
  const norm = 4 / n; // Hann: a sine of amplitude A peaks at about A * n / 4.
  const frame = new Float64Array(n);
  const mag = new Float64Array(half + 1);
  let prev = new Float64Array(bands);
  let cur = new Float64Array(bands);
  const progressEvery = Math.max(1, Math.floor(frames / 40));
  for (let i = 0; i < frames; i++) {
    const start = i * hop - n + lead;
    for (let t = 0; t < n; t++) {
      const s = start + t;
      const v = s >= 0 && s < mono.length ? (mono[s] ?? 0) : 0;
      frame[t] = (Number.isFinite(v) ? v : 0) * (win[t] ?? 0);
    }
    fft.magnitudes(frame, mag);
    let flux = 0;
    for (let b = 0; b < bands; b++) {
      const a = edges[b] ?? 0;
      const z = edges[b + 1] ?? a + 1;
      let sum = 0;
      for (let k = a; k < z; k++) sum += mag[k] ?? 0;
      const y = Math.log1p((COMPRESSION * norm * sum) / (z - a));
      cur[b] = y;
      const d = y - (prev[b] ?? 0);
      if (d > 0 && i > 0) flux += d;
    }
    out[i] = flux;
    const tmp = prev;
    prev = cur;
    cur = tmp;
    if (opts.onProgress !== undefined && i % progressEvery === 0) opts.onProgress(i / frames);
  }
  opts.onProgress?.(1);
  return out;
}

/** Subtracts a centred moving mean (window `w` frames), rectifies and scales the maximum to 1. */
export function normalizeEnvelope(raw: Float64Array, w: number): Float32Array {
  const n = raw.length;
  const out = new Float32Array(n);
  if (n === 0) return out;
  const r = Math.max(1, Math.floor(w / 2));
  // Prefix sums for the moving mean.
  const prefix = new Float64Array(n + 1);
  for (let i = 0; i < n; i++) prefix[i + 1] = (prefix[i] ?? 0) + (raw[i] ?? 0);
  let max = 0;
  for (let i = 0; i < n; i++) {
    const a = Math.max(0, i - r);
    const b = Math.min(n, i + r + 1);
    const mean = ((prefix[b] ?? 0) - (prefix[a] ?? 0)) / (b - a);
    const v = Math.max(0, (raw[i] ?? 0) - mean);
    out[i] = v;
    if (v > max) max = v;
  }
  if (max > 0) for (let i = 0; i < n; i++) out[i] = (out[i] ?? 0) / max;
  return out;
}

/** The onset envelope of a mono signal (see the module header). */
export function onsetEnvelope(mono: Float32Array, sampleRate: number, opts: OnsetOptions = {}): OnsetEnvelope {
  if (!(sampleRate > 0)) throw new RangeError(`onsetEnvelope: bad sample rate ${String(sampleRate)}`);
  const hop = opts.hop ?? ONSET_HOP;
  const hopSec = hop / sampleRate;
  const raw = spectralFlux(mono, sampleRate, opts);
  return { hopSec, env: normalizeEnvelope(raw, Math.round(FLOOR_WINDOW_SEC / hopSec)) };
}

/** The onset envelope of the low band only (30-200 Hz: kick drum and bass; see the module header). */
export function lowOnsetEnvelope(mono: Float32Array, sampleRate: number, opts: OnsetOptions = {}): OnsetEnvelope {
  return onsetEnvelope(mono, sampleRate, { ...opts, minFreq: MIN_FREQ, maxFreq: LOW_BAND_MAX_FREQ });
}

/** Envelope value at music time `sec` (linear interpolation; 0 outside). */
export function envelopeAt(e: OnsetEnvelope, sec: number): number {
  const x = sec / e.hopSec;
  if (!(x >= 0)) return 0;
  const i = Math.floor(x);
  if (i >= e.env.length) return 0;
  const f = x - i;
  const a = e.env[i] ?? 0;
  const b = i + 1 < e.env.length ? (e.env[i + 1] ?? 0) : 0;
  return a + (b - a) * f;
}

/** Largest envelope value over the music time range [from, to] (interpolated at the ends). */
export function envelopeMax(e: OnsetEnvelope, from: number, to: number): number {
  if (!(to >= from)) return 0;
  let best = Math.max(envelopeAt(e, from), envelopeAt(e, to));
  const i0 = Math.max(0, Math.ceil(from / e.hopSec));
  const i1 = Math.min(e.env.length - 1, Math.floor(to / e.hopSec));
  for (let i = i0; i <= i1; i++) best = Math.max(best, e.env[i] ?? 0);
  return best;
}

/** Hop and frame of `fineOnsetCurve` (samples). */
export const FINE_HOP = 32;
export const FINE_FRAME = 256;

export interface FineCurve {
  /** Music time of value 0. */
  readonly startSec: number;
  readonly stepSec: number;
  readonly values: Float64Array;
}

/**
 * High-resolution novelty of the PCM between `fromSec` and `toSec`: for each 32-sample step, the positive change of
 * log energy between the 256-sample frame ending at the step and the one ending a step earlier, stamped at the
 * middle of the newly added samples. A sharp attack peaks within half a step (0.4 ms at 44.1 kHz) of its start.
 */
export function fineOnsetCurve(mono: Float32Array, sampleRate: number, fromSec: number, toSec: number): FineCurve {
  const hop = FINE_HOP;
  const frame = FINE_FRAME;
  const first = Math.max(0, Math.floor((fromSec * sampleRate) / hop) * hop);
  const last = Math.min(mono.length, Math.ceil(toSec * sampleRate));
  const steps = Math.max(0, Math.floor((last - first) / hop));
  const values = new Float64Array(steps);
  const energyEnding = (end: number): number => {
    let e = 0;
    const s0 = Math.max(0, end - frame);
    for (let s = s0; s < end; s++) {
      const v = s < mono.length ? (mono[s] ?? 0) : 0;
      e += v * v;
    }
    return e / frame;
  };
  const floor = 1e-9;
  let prevLog = Math.log(energyEnding(first) + floor);
  for (let j = 0; j < steps; j++) {
    const end = first + (j + 1) * hop;
    const lg = Math.log(energyEnding(end) + floor);
    values[j] = Math.max(0, lg - prevLog);
    prevLog = lg;
  }
  return { startSec: (first + hop / 2) / sampleRate, stepSec: hop / sampleRate, values };
}

/** Smallest log-energy step accepted as an attack by `exactOnsetNear` (about +1.7 dB in one step). */
const MIN_FINE_STEP = 0.4;

/**
 * Exact time (s) of the sharpest attack within `windowSec` of `approxSec`: the maximum of `fineOnsetCurve` there
 * (sub-millisecond), or null when no step reaches MIN_FINE_STEP (no clear attack, e.g. a swell or noise).
 */
export function exactOnsetNear(
  mono: Float32Array,
  sampleRate: number,
  approxSec: number,
  windowSec: number,
): number | null {
  const w = Math.max(0, windowSec);
  const curve = fineOnsetCurve(mono, sampleRate, approxSec - w, approxSec + w);
  let best = -1;
  let at = -1;
  for (let j = 0; j < curve.values.length; j++) {
    const v = curve.values[j] ?? 0;
    if (v > best) {
      best = v;
      at = j;
    }
  }
  return at < 0 || best < MIN_FINE_STEP ? null : curve.startSec + at * curve.stepSec;
}
